// =============================================================================
// src/features/analytics/analytics-insights-card-parser.ts - Parse de UM card
// tatico (do heading `### CARD NN — ...` ate o separador `---`) para o tipo
// AnalyticsInsightCard. Falha DURA (marcador/impacto/classificacao invalidos)
// devolve card null + issue — o parser de edicao decide o fallback generico.
// Falha BRANDA (citacao sem atribuicao, secao vazia) degrada localmente.
// =============================================================================

import type { AnalyticsInsightMarkdownBlock } from './analytics-insights-markdown-blocks';
import {
  analyticsInsightInlineHtml,
  analyticsInsightJoinHardWrap,
  analyticsInsightPlainText,
} from './analytics-insights-markdown-inline';
import type {
  AnalyticsInsightCard,
  AnalyticsInsightClassification,
  AnalyticsInsightEvidenceBlock,
  AnalyticsInsightImpactScore,
} from './analytics-insights-types';

/** Problema encontrado no parse; detail carrega o valor ofensor (regra 11). */
export interface AnalyticsInsightParseIssue {
  readonly issueCode: string;
  readonly detail: string;
}

/** Lista fechada em runtime — espelho da uniao AnalyticsInsightClassification. */
const ANALYTICS_INSIGHT_KNOWN_CLASSIFICATIONS: readonly AnalyticsInsightClassification[] = [
  'AMEAÇA ESTRUTURAL', 'AMEAÇA DE PAUTA', 'OPORTUNIDADE', 'OPORTUNIDADE PERDIDA',
  'TERRENO DE IMPRENSA', 'VIGILÂNCIA', 'CORREÇÃO DE ROTA', 'LIMITE DE MEDIÇÃO',
];

const REGEX_CARD_HEADING = /^CARD (\d{2}) — (.+)$/;
const REGEX_IMPACT_MARKER = /^`IMPACTO ([1-5])\/5`\s*·\s*`([^`]+)`$/;
const CARD_SECTION_EVIDENCE = '**O QUE OS DADOS MOSTRAM**';
const CARD_SECTION_READING = '**LEITURA TÁTICA**';
const CARD_SECTION_ACTION = '**AÇÃO**';

/** Resultado do parse de um card: ou o card, ou null com o motivo em issues. */
export interface AnalyticsInsightCardParseOutcome {
  readonly card: AnalyticsInsightCard | null;
  readonly issues: readonly AnalyticsInsightParseIssue[];
}

/** Parseia um card a partir do texto do heading H3 + blocos ate o `---`. */
export function parseAnalyticsInsightCard(
  cardHeadingText: string,
  cardBlocks: readonly AnalyticsInsightMarkdownBlock[],
  priorityTitleByCardNumber: ReadonlyMap<number, string>,
): AnalyticsInsightCardParseOutcome {
  const issues: AnalyticsInsightParseIssue[] = [];
  const headingMatch = REGEX_CARD_HEADING.exec(cardHeadingText);
  if (headingMatch?.[1] === undefined || headingMatch[2] === undefined) {
    return refuseCard('card_heading_invalido', cardHeadingText);
  }
  const cardNumber = Number(headingMatch[1]);
  const marker = parseAnalyticsInsightImpactMarker(cardBlocks[0]);
  if (marker === null) {
    return refuseCard('card_marcador_impacto_ausente', `${cardHeadingText}: ${describeBlock(cardBlocks[0])}`);
  }
  if (!isKnownClassification(marker.classificationLabel)) {
    return refuseCard('card_classificacao_desconhecida', marker.classificationLabel);
  }
  const sections = splitAnalyticsInsightCardSections(cardBlocks.slice(1), issues);
  return {
    card: {
      cardNumber,
      cardTitle: analyticsInsightPlainText(headingMatch[2]),
      priorityTitle: priorityTitleByCardNumber.get(cardNumber) ?? analyticsInsightPlainText(headingMatch[2]),
      impactScore: marker.impactScore,
      classificationLabel: marker.classificationLabel,
      evidenceBlocks: sections.evidenceBlocks,
      tacticalReadingHtml: sections.tacticalReadingHtml,
      actionCallHtml: sections.actionCallHtml,
      ...parseAnalyticsInsightMetaFooter(sections.metaLine, issues),
    },
    issues,
  };
}

function refuseCard(issueCode: string, detail: string): AnalyticsInsightCardParseOutcome {
  return { card: null, issues: [{ issueCode, detail }] };
}

function describeBlock(markdownBlock: AnalyticsInsightMarkdownBlock | undefined): string {
  if (markdownBlock === undefined) return 'sem blocos apos o heading';
  if (markdownBlock.mdKind === 'paragraph') return markdownBlock.paragraphLines.join(' ');
  return markdownBlock.mdKind;
}

function isKnownClassification(candidate: string): candidate is AnalyticsInsightClassification {
  return (ANALYTICS_INSIGHT_KNOWN_CLASSIFICATIONS as readonly string[]).includes(candidate);
}

function parseAnalyticsInsightImpactMarker(
  markerBlock: AnalyticsInsightMarkdownBlock | undefined,
): { impactScore: AnalyticsInsightImpactScore; classificationLabel: string } | null {
  if (markerBlock?.mdKind !== 'paragraph') return null;
  const markerMatch = REGEX_IMPACT_MARKER.exec(analyticsInsightJoinHardWrap(markerBlock.paragraphLines));
  if (markerMatch?.[1] === undefined || markerMatch[2] === undefined) return null;
  return {
    impactScore: Number(markerMatch[1]) as AnalyticsInsightImpactScore,
    classificationLabel: markerMatch[2].trim(),
  };
}

interface AnalyticsInsightCardSections {
  readonly evidenceBlocks: readonly AnalyticsInsightEvidenceBlock[];
  readonly tacticalReadingHtml: readonly string[];
  readonly actionCallHtml: string;
  readonly metaLine: string;
}

/** Percorre os blocos pos-marcador roteando pelos marcadores de secao. */
function splitAnalyticsInsightCardSections(
  bodyBlocks: readonly AnalyticsInsightMarkdownBlock[],
  issues: AnalyticsInsightParseIssue[],
): AnalyticsInsightCardSections {
  const evidenceBlocks: AnalyticsInsightEvidenceBlock[] = [];
  const tacticalReadingHtml: string[] = [];
  let actionCallHtml = '';
  let metaLine = '';
  let currentSection: 'evidence' | 'reading' | 'action' = 'evidence';
  for (const bodyBlock of bodyBlocks) {
    const sectionSwitch = analyticsInsightSectionForMarker(bodyBlock);
    if (sectionSwitch !== null) {
      currentSection = sectionSwitch;
      continue;
    }
    if (isAnalyticsInsightMetaParagraph(bodyBlock)) {
      metaLine = analyticsInsightJoinHardWrap(bodyBlock.paragraphLines);
      continue;
    }
    if (currentSection === 'evidence') appendAnalyticsInsightEvidence(bodyBlock, evidenceBlocks, issues);
    else if (currentSection === 'reading') appendAnalyticsInsightReading(bodyBlock, tacticalReadingHtml);
    else actionCallHtml = appendAnalyticsInsightAction(bodyBlock, actionCallHtml);
  }
  if (actionCallHtml === '') issues.push({ issueCode: 'card_sem_acao', detail: 'secao **AÇÃO** vazia' });
  return { evidenceBlocks, tacticalReadingHtml, actionCallHtml, metaLine };
}

function analyticsInsightSectionForMarker(
  markdownBlock: AnalyticsInsightMarkdownBlock,
): 'evidence' | 'reading' | 'action' | null {
  if (markdownBlock.mdKind !== 'paragraph' || markdownBlock.paragraphLines.length !== 1) return null;
  const markerLine = markdownBlock.paragraphLines[0];
  if (markerLine === CARD_SECTION_EVIDENCE) return 'evidence';
  if (markerLine === CARD_SECTION_READING) return 'reading';
  if (markerLine === CARD_SECTION_ACTION) return 'action';
  return null;
}

function isAnalyticsInsightMetaParagraph(
  markdownBlock: AnalyticsInsightMarkdownBlock,
): markdownBlock is { mdKind: 'paragraph'; paragraphLines: readonly string[] } {
  return (
    markdownBlock.mdKind === 'paragraph' &&
    /* v8 ignore next -- o scanner nunca emite paragrafo sem linhas; ?? false e defensivo (noUncheckedIndexedAccess) */
    (markdownBlock.paragraphLines[0]?.startsWith('**Confiança:**') ?? false)
  );
}

function appendAnalyticsInsightEvidence(
  markdownBlock: AnalyticsInsightMarkdownBlock,
  evidenceBlocks: AnalyticsInsightEvidenceBlock[],
  issues: AnalyticsInsightParseIssue[],
): void {
  if (markdownBlock.mdKind === 'paragraph') {
    evidenceBlocks.push({
      blockKind: 'paragraph',
      paragraphHtml: analyticsInsightInlineHtml(analyticsInsightJoinHardWrap(markdownBlock.paragraphLines)),
    });
  } else if (markdownBlock.mdKind === 'table') {
    evidenceBlocks.push({
      blockKind: 'table',
      columnHeaders: markdownBlock.headerCells.map(analyticsInsightPlainText),
      tableRows: markdownBlock.rowsCells.map((rowCells) => rowCells.map(analyticsInsightInlineHtml)),
    });
  } else if (markdownBlock.mdKind === 'blockquote') {
    evidenceBlocks.push(parseAnalyticsInsightQuote(markdownBlock.quoteLines, issues));
  }
}

/** Citacao `*"..."* — atribuicao` (apos o re-join, atribuicao na mesma linha
 * e em linha propria convergem p/ a mesma forma); sem atribuicao degrada p/
 * paragrafo com issue. */
function parseAnalyticsInsightQuote(
  quoteLines: readonly string[],
  issues: AnalyticsInsightParseIssue[],
): AnalyticsInsightEvidenceBlock {
  const joinedQuote = analyticsInsightJoinHardWrap(quoteLines);
  const quoteMatch = /^\*(.+?)\*\s*—\s*(.+)$/.exec(joinedQuote);
  if (quoteMatch?.[1] !== undefined && quoteMatch[2] !== undefined) {
    return {
      blockKind: 'quote',
      quoteHtml: analyticsInsightInlineHtml(quoteMatch[1].trim()),
      quoteAttribution: analyticsInsightPlainText(quoteMatch[2]),
    };
  }
  issues.push({ issueCode: 'citacao_sem_atribuicao', detail: joinedQuote.slice(0, 120) });
  return { blockKind: 'paragraph', paragraphHtml: analyticsInsightInlineHtml(joinedQuote) };
}

function appendAnalyticsInsightReading(
  markdownBlock: AnalyticsInsightMarkdownBlock,
  tacticalReadingHtml: string[],
): void {
  if (markdownBlock.mdKind !== 'paragraph') return;
  tacticalReadingHtml.push(analyticsInsightInlineHtml(analyticsInsightJoinHardWrap(markdownBlock.paragraphLines)));
}

function appendAnalyticsInsightAction(
  markdownBlock: AnalyticsInsightMarkdownBlock,
  actionCallHtml: string,
): string {
  if (markdownBlock.mdKind === 'blockquote') {
    return analyticsInsightInlineHtml(analyticsInsightJoinHardWrap(markdownBlock.quoteLines));
  }
  return actionCallHtml;
}

const ANALYTICS_INSIGHT_META_LABELS = ['Confiança', 'Natureza', 'Base', 'Validade', 'Série'] as const;

/** Rodape de 5 campos: split pelos ROTULOS `**X:**` (os valores contem `·` e
 * `—` internos — split ingenuo por separador quebraria o campo Serie). */
function parseAnalyticsInsightMetaFooter(
  metaLine: string,
  issues: AnalyticsInsightParseIssue[],
): Pick<AnalyticsInsightCard, 'confidenceLine' | 'natureLine' | 'baseLine' | 'validityLine' | 'seriesLine'> {
  const metaValues = ANALYTICS_INSIGHT_META_LABELS.map((metaLabel, labelIndex) => {
    const nextLabel = ANALYTICS_INSIGHT_META_LABELS[labelIndex + 1];
    const fieldPattern = new RegExp(
      `\\*\\*${metaLabel}:\\*\\*\\s*(.*?)\\s*(?:·\\s*)?${nextLabel === undefined ? '$' : `(?=\\*\\*${nextLabel}:\\*\\*)`}`,
    );
    return fieldPattern.exec(metaLine)?.[1] ?? null;
  });
  if (metaValues.some((metaValue) => metaValue === null)) {
    issues.push({ issueCode: 'card_rodape_incompleto', detail: metaLine.slice(0, 160) });
  }
  const [confidenceLine, natureLine, baseLine, validityLine, seriesLine] = metaValues.map((metaValue) =>
    analyticsInsightPlainText(metaValue ?? ''),
  );
  /* v8 ignore next 3 -- map sobre a tupla de 5 rotulos sempre devolve 5; ?? '' e defensivo (noUncheckedIndexedAccess) */
  return {
    confidenceLine: confidenceLine ?? '', natureLine: natureLine ?? '', baseLine: baseLine ?? '',
    validityLine: validityLine ?? '', seriesLine: seriesLine ?? '',
  };
}
